<template>
  <div class="animated fadeIn">
    <!-- Cabeçalho -->
    <div class="row">
      <div class="col-12 pt-3 pb-3 pl-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h3><i class="fa fa-sync"></i> Sincronização Manual de Usuários AD</h3>
            <small>Pesquise e importe usuários do Active Directory que ainda não estão cadastrados no sistema.</small>
          </div>
          <button
            class="btn btn-third"
            @click="searchUsers"
            :disabled="loading"
          >
            <i class="fa fa-search"></i> Pesquisar usuários AD
          </button>
        </div>
      </div>
    </div>

    <!-- Skeleton Loading -->
    <div v-if="loading" class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div v-for="n in 5" :key="n" class="skeleton-row mb-3">
              <div class="skeleton-checkbox"></div>
              <div class="skeleton-text" style="width: 15%"></div>
              <div class="skeleton-text" style="width: 25%"></div>
              <div class="skeleton-text" style="width: 20%"></div>
              <div class="skeleton-text" style="width: 20%"></div>
              <div class="skeleton-text" style="width: 15%"></div>
              <div class="skeleton-badge"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabela de Usuários -->
    <div v-if="!loading" class="row mt-3">
      <div class="col-12">
        <div class="card">
        <!-- Observação sobre limite -->
          <div class="card-body pt-3 pb-2 px-3" style="border-bottom: 1px solid #dcdede;">
            <small class="text-muted">
              <i class="fa fa-info-circle"></i> <strong>Atenção:</strong> A importação manual está limitada a 50 usuários por vez.
            </small>
          </div>
          <div class="card-header text-third">
            <strong><i class="fa fa-users"></i> Usuários Disponíveis para Importação</strong>
            <div class="card-header-actions">
              <button
                class="btn btn-success btn-sm"
                @click="showImportModal"
                :disabled="selectedUsers.length === 0 || importing"
              >
                <i class="fa fa-download"></i> Importar usuários selecionados ({{ selectedUsers.length }})
              </button>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover" style="border: 1px solid #dcdede;">
                <thead class="thead-third" style="background-color: #04a791; color: #fff; border: solid 1px #04a791; height: 44px">
                  <tr>
                    <th style="width: 50px;" class="text-center">
                      <input
                        type="checkbox"
                        :checked="allUsersSelected"
                        @change="toggleSelectAll"
                        class="form-check-input"
                        style="width: 18px; height: 18px; cursor: pointer;"
                        :disabled="users.length === 0"
                      >
                    </th>
                    <th class="text-center">Login</th>
                    <th class="text-center">Nome</th>
                    <th class="text-center">Email</th>
                    <th class="text-center">Departamentos</th>
                    <th class="text-center">Grupos AD</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody v-if="users.length > 0">
                  <tr v-for="user in users" :key="user.username">
                    <td class="text-center" style="padding: 12px;">
                      <input
                        type="checkbox"
                        :value="user.username"
                        v-model="selectedUsers"
                        class="form-check-input"
                        style="width: 18px; height: 18px; cursor: pointer;"
                      >
                    </td>
                    <td>
                      <strong>{{ user.username }}</strong>
                    </td>
                    <td>{{ user.nome }}</td>
                    <td>{{ user.email }}</td>
                    <td>{{ user.departamento || '-' }}</td>
                    <td>
                      <span
                        v-for="(grupo, index) in user.gruposAd"
                        :key="index"
                        class="badge badge-secondary mr-1 mb-1"
                      >
                        {{ extractGroupName(grupo) }}
                      </span>
                      <span v-if="!user.gruposAd || user.gruposAd.length === 0" class="text-muted">-</span>
                    </td>
                    <td class="text-center">
                      <span
                        v-if="user.ativo"
                        class="badge badge-success"
                        style="width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: 5px;"
                      ></span>
                      <span
                        v-else
                        class="badge badge-danger"
                        style="width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: 5px;"
                      ></span>
                      {{ user.status }}
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="7" class="text-center py-5">
                      <i class="fa fa-users fa-4x text-muted mb-3 d-block"></i>
                      <p class="text-muted mb-2">Nenhum usuário disponível para importação.</p>
                      <small class="text-muted">
                        Clique em "Pesquisar usuários AD" para buscar usuários ou todos os usuários dos grupos da conta de serviço já estão cadastrados no sistema.
                      </small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Importação -->
    <b-modal
      v-model="showConfirmModal"
      title="Confirmar Importação"
      @ok="importUsers"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      ok-variant="success"
    >
      <p>
        Deseja importar <strong>{{ selectedUsers.length }}</strong> usuário(s) selecionado(s)?
      </p>
      <p class="text-muted small">
        Os usuários serão criados no sistema com as permissões e acessos configurados para seus grupos AD.
      </p>
    </b-modal>

    <!-- Modal de Resultado -->
    <b-modal
      v-model="showResultModal"
      title="Resultado da Importação"
      ok-only
      ok-title="Fechar"
      @hidden="reloadUsersList"
    >
      <div v-if="importResult">
        <h5>Resumo:</h5>
        <ul>
          <li><strong>Total:</strong> {{ importResult.totalUsers }}</li>
          <li class="text-success">
            <strong>Criados:</strong> {{ importResult.createdUsers }}
          </li>
          <li class="text-info">
            <strong>Atualizados:</strong> {{ importResult.updatedUsers }}
          </li>
          <li class="text-danger">
            <strong>Falhas:</strong> {{ importResult.failedUsers }}
          </li>
        </ul>

        <div v-if="importResult.failedUsers > 0" class="mt-3">
          <h6 class="text-danger">Falhas:</h6>
          <ul>
            <li v-for="(result, index) in failedResults" :key="index">
              <strong>{{ result.username }}</strong>: {{ result.errorMessage }}
            </li>
          </ul>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { loading, toast } from '@/shared/utils/UIHelper'
import adSyncService from '@/shared/http/repositories/adsync/adsync'

export default {
  name: 'ManualSync',
  data () {
    return {
      users: [],
      selectedUsers: [],
      loading: false,
      hasSearched: false,
      importing: false,
      showConfirmModal: false,
      showResultModal: false,
      importResult: null
    }
  },
  computed: {
    failedResults () {
      if (!this.importResult || !this.importResult.results) return []
      return this.importResult.results.filter(r => r.status === 'FAILED')
    },
    allUsersSelected () {
      if (this.users.length === 0) return false
      return this.selectedUsers.length === this.users.length
    }
  },
  methods: {
    async searchUsers () {
      this.loading = true
      this.hasSearched = false
      this.users = []
      this.selectedUsers = []

      try {
        loading.push()
        const response = await adSyncService.getAvailableUsers()
        this.users = response.data || []
        this.hasSearched = true

        if (this.users.length === 0) {
          toast.info('Nenhum usuário disponível para importação')
        } else {
          toast.success(`${this.users.length} usuário(s) encontrado(s)`)
        }
      } catch (error) {
        console.error('Erro ao buscar usuários', error)
        toast.error('Erro ao buscar usuários do AD. Verifique a conexão.')
      } finally {
        this.loading = false
        loading.pop()
      }
    },
    showImportModal () {
      if (this.selectedUsers.length === 0) {
        toast.warning('Selecione pelo menos um usuário')
        return
      }

      if (this.selectedUsers.length > 50) {
        toast.error('Máximo de 50 usuários por importação')
        return
      }

      this.showConfirmModal = true
    },
    async importUsers () {
      this.showConfirmModal = false
      this.importing = true

      try {
        loading.push()
        const response = await adSyncService.syncUsers(this.selectedUsers)

        this.importResult = response.data
        this.showResultModal = true

        if (this.importResult.failedUsers === 0) {
          toast.success('Importação concluída com sucesso!')
        } else {
          toast.warning(`Importação concluída com ${this.importResult.failedUsers} falha(s)`)
        }
      } catch (error) {
        console.error('Erro na importação', error)
        const errorMessage = (error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : (error.message || 'Erro desconhecido')
        toast.error('Erro na importação: ' + errorMessage)
      } finally {
        this.importing = false
        loading.pop()
      }
    },
    extractGroupName (groupDN) {
      if (!groupDN) return '-'
      // Extrair CN do DN: CN=TI_Desenvolvimento,OU=Groups... -> TI_Desenvolvimento
      const match = groupDN.match(/CN=([^,]+)/)
      return match ? match[1] : groupDN
    },
    toggleSelectAll (event) {
      if (event.target.checked) {
        // Selecionar todos os usuários
        this.selectedUsers = this.users.map(user => user.username)
      } else {
        // Desselecionar todos
        this.selectedUsers = []
      }
    },
    async reloadUsersList () {
      // Recarregar lista após fechar o modal de resultado da importação
      if (this.hasSearched) {
        await this.searchUsers()
      }
    }
  }
}
</script>

<style scoped>
.skeleton-row {
  display: flex;
  align-items: center;
  gap: 15px;
}

.skeleton-checkbox {
  width: 20px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-badge {
  width: 60px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 12px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header-actions {
  display: flex;
  gap: 10px;
}

</style>



// WEBPACK FOOTER //
// src/views/ADSync/ManualSync.vue